// src/hooks/useAppointmentRequests.ts

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
  addDoc,
  getDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, getCloudFunctionUrl } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { useAuth } from '../contexts/AuthContext';
import {
  AppointmentRequest,
  AppointmentRequestFormData,
  AppointmentRequestSubmitResult,
} from '../types/appointmentRequest';

// Hook pour récupérer les demandes de RDV en attente (TEMPS RÉEL)
export const useAppointmentRequests = () => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const [requests, setRequests] = useState<AppointmentRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    console.log('🔍 [useAppointmentRequests] Setup listener temps réel');
    console.log('🔍 [useAppointmentRequests] BusinessId:', businessId);
    
    if (!businessId) {
      console.log('⚠️ [useAppointmentRequests] Pas de businessId');
      setRequests([]);
      setIsLoading(false);
      return;
    }

    // ✅ PHASE 1.4 : Pause listener si l'onglet est en arrière-plan
    if (document.hidden) {
      console.log('⏸️ [useAppointmentRequests] Onglet inactif, listener en pause');
      setRequests([]);
      setIsLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, COLLECTIONS.appointmentRequests),
        where('businessId', '==', businessId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      );

      console.log('🔍 [useAppointmentRequests] Création listener onSnapshot...');
      
      // ✅ PHASE 1.4 : Listener temps réel optimisé (pas de metadata changes)
      const unsubscribe = onSnapshot(
        q,
        { includeMetadataChanges: false }, // Réduire les mises à jour inutiles
        (snapshot) => {
          console.log(`✅ [useAppointmentRequests] Snapshot reçu: ${snapshot.size} demande(s)`);
          
          const results = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              businessId: data.businessId,
              clientId: data.clientId || null,
              isExistingClient: data.isExistingClient || false,
              name: data.name,
              phone: data.phone,
              email: data.email || undefined,
              requestedService: data.requestedService,
              requestedDate: data.requestedDate?.toDate(),
              requestedTime: data.requestedTime || undefined, // Format "HH:mm"
              message: data.message || undefined,
              status: data.status,
              source: data.source,
              createdAt: data.createdAt?.toDate(),
              ipAddress: data.ipAddress,
              existingClientInfo: data.existingClientInfo,
              processedAt: data.processedAt?.toDate(),
              processedBy: data.processedBy,
              appointmentId: data.appointmentId,
              rejectionReason: data.rejectionReason,
            } as AppointmentRequest;
          });
          
          setRequests(results);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('❌ [useAppointmentRequests] Erreur onSnapshot:', err);
          setError(err as Error);
          setIsLoading(false);
        }
      );

      // ✅ PHASE 1.4 : Écouter les changements de visibilité de l'onglet
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log('⏸️ [useAppointmentRequests] Onglet caché, déconnexion listener');
          unsubscribe();
        } else {
          console.log('▶️ [useAppointmentRequests] Onglet visible, reconnexion listener');
          // Le useEffect se relancera automatiquement
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        console.log('🔌 [useAppointmentRequests] Déconnexion listener');
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        unsubscribe();
      };
    } catch (err: any) {
      console.error('❌ [useAppointmentRequests] Erreur setup:', err);
      setError(err);
      setIsLoading(false);
    }
  }, [businessId, document.hidden]);

  return { data: requests, isLoading, error };
};

// Hook pour compter les demandes en attente
export const useAppointmentRequestsCount = () => {
  const { data: requests = [] } = useAppointmentRequests();
  return requests.length;
};

// Hook pour soumettre une demande depuis la landing page (PUBLIQUE - pas d'auth)
export const useSubmitAppointmentRequest = () => {
  return useMutation<AppointmentRequestSubmitResult, Error, AppointmentRequestFormData>({
    mutationFn: async (formData: AppointmentRequestFormData) => {
      console.log('📝 Soumission demande RDV:', formData);
      
      // ✅ CORRIGÉ : URL dynamique selon l'environnement
      const functionUrl = getCloudFunctionUrl('submitAppointmentRequest', 'us-central1');
      
      console.log('🔗 URL Cloud Function:', functionUrl);
      
      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erreur réseau' }));
        throw new Error(errorData.error || 'Erreur lors de la soumission');
      }
      
      const result: AppointmentRequestSubmitResult = await response.json();
      console.log('✅ Résultat:', result);
      
      return result;
    },
  });
};

// Hook pour confirmer une demande (crée le client si besoin + RDV)
export const useConfirmAppointmentRequest = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, user } = useAuth();
const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async ({
      requestId,
      appointmentData,
    }: {
      requestId: string;
      appointmentData?: {
        date: Date;
        startTime: string;
        title?: string;
        notes?: string;
      };
    }) => {
      console.log('📝 [useConfirmAppointmentRequest] Traitement demande:', requestId);
      
      // 1. Récupérer les données de la demande
      const requestRef = doc(db, COLLECTIONS.appointmentRequests, requestId);
      const requestSnap = await getDoc(requestRef);
      
      if (!requestSnap.exists()) {
        throw new Error('Demande introuvable');
      }
      
      const request = requestSnap.data();
      console.log('📋 [useConfirmAppointmentRequest] Données demande:', request);
      
      let clientId = request.clientId;
      
      // 2. Si nouveau client → Créer le client
      if (!request.isExistingClient) {
        console.log('➕ [useConfirmAppointmentRequest] Création nouveau client');
        
        const newClient = {
          businessId: businessId,
          name: request.name,
          phone: request.phone,
          email: request.email || null,
          createdAt: Timestamp.now(),
          createdBy: user?.uid || 'system',
          updatedAt: Timestamp.now(),
          // Champs par défaut
          loyaltyBalance: 0,
          totalSpent: 0,
          visitCount: 0,
          isActive: true,
          preferredServices: [request.requestedService],
          notes: `Client créé depuis une demande de RDV pour ${request.requestedService}`,
        };
        
        const clientDocRef = await addDoc(collection(db, COLLECTIONS.clients), newClient);
        clientId = clientDocRef.id;
        console.log('✅ [useConfirmAppointmentRequest] Client créé:', clientId);
      }
      
      // 3. Créer le rendez-vous
      console.log('📅 [useConfirmAppointmentRequest] Création RDV');
      
      const appointmentDate = appointmentData?.date || request.requestedDate;
      // Utiliser l'heure de la demande si fournie, sinon celle du formulaire, sinon 10:00 par défaut
      const appointmentTime = appointmentData?.startTime || request.requestedTime || '10:00';
      
      const newAppointment = {
        businessId: businessId,
        clientId: clientId,
        clientName: request.name,
        clientPhone: request.phone,
        title: appointmentData?.title || request.requestedService,
        date: Timestamp.fromDate(appointmentDate),
        startTime: appointmentTime,
        duration: 60, // 1h par défaut
        status: 'confirmed',
        notes: appointmentData?.notes || request.message || `RDV créé depuis demande en ligne`,
        createdBy: user?.uid || 'system',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Champs optionnels
        service: request.requestedService,
        source: 'appointment_request',
      };
      
      const appointmentDocRef = await addDoc(collection(db, COLLECTIONS.appointments), newAppointment);
      console.log('✅ [useConfirmAppointmentRequest] RDV créé:', appointmentDocRef.id);
      
      // 4. Mettre à jour la demande
      await updateDoc(requestRef, {
        status: 'confirmed',
        processedAt: Timestamp.now(),
        processedBy: user?.uid,
        clientId: clientId,
        appointmentId: appointmentDocRef.id,
      });
      
      console.log('✅ [useConfirmAppointmentRequest] Demande mise à jour');
      
      return {
        success: true,
        clientId,
        appointmentId: appointmentDocRef.id,
        isNewClient: !request.isExistingClient,
      };
    },
    onSuccess: () => {
      console.log('🔄 [useConfirmAppointmentRequest] Invalidation caches');
      // Rafraîchir les listes
      queryClient.invalidateQueries({ queryKey: ['appointmentRequests', businessId] });
      queryClient.invalidateQueries({ queryKey: ['clients', businessId] });
      queryClient.invalidateQueries({ queryKey: ['appointments', businessId] });
    },
  });
};

// Hook pour rejeter une demande
export const useRejectAppointmentRequest = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, user } = useAuth();
const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async ({
      requestId,
      reason,
    }: {
      requestId: string;
      reason?: string;
    }) => {
      const requestRef = doc(db, COLLECTIONS.appointmentRequests, requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        processedAt: Timestamp.now(),
        processedBy: user?.uid,
        rejectionReason: reason || 'Non spécifié',
      });
      
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appointmentRequests', businessId] });
    },
  });
};

